import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Form, Button, Select, notification, Flex } from 'antd';

import ResetPassword from '@controls/reset-password/reset-password';

import { MainLogoIcon } from '@icons/index';

import { jwtDecode } from 'jwt-decode';

import { getEnumList, tryParseJson } from '@extensions/utils';
import { exception } from '@extensions/notification';
import { isNewYearAroundUs } from '@extensions/utils';
import { userLoaded } from '@store/actions';

import { IUserSession } from '@entities/user-session';

import { BusinessUnit, enumLabel } from '@enums/business-unit';
import { IEnumItem } from '@enums/enum-item';
import { UserType } from '@enums/user-type';

import { serverFetch } from '@core/server';

import TelegramCuttingBotQr from '@assets/svg/TelegramCuttingBotQr.svg';
import TelegramPlantingBotQr from '@assets/svg/TelegramPlantingBotQr.svg';
import MaxCuttingBotQr from '@assets/svg/MaxCuttingBotQr.svg';
import MaxPlantingBotQr from '@assets/svg/MaxPlantingBotQr.svg';


import '@core/auth.css';

const flcApiBaseUrl = process.env.REACT_APP_FLC_API_BASE_URL;
const flpApiBaseUrl = process.env.REACT_APP_FLP_API_BASE_URL;

interface IAuth {
    appVersion: string | undefined;
}

interface IAuthParams {
    businessUnit: BusinessUnit;
    signInLogin: string;
    password: string;
}

const Auth = (props: IAuth) => {
    const { appVersion } = props;

    const navigate = useNavigate();

    const d = useDispatch();

    const [form] = Form.useForm();
    const businessUnit = Form.useWatch<BusinessUnit>('businessUnit', form);

    const [api, contextHolder] = notification.useNotification();

    const [loading, setLoading] = useState(false);
    const [ipAddress, setIpAddress] = useState<string>();

    const [businessUnits] = useState<Array<IEnumItem>>(getEnumList(BusinessUnit, enumLabel));
    const [apiBaseUrl, setApiBaseUrl] = useState<string>();

    const [showResetPassword, setShowResetPassword] = useState<boolean>(false);

    useEffect(() => {
        const storedBusinessUnit = localStorage.getItem('businessUnit');
        if (storedBusinessUnit) {
            const businessUnit: BusinessUnit = +storedBusinessUnit as BusinessUnit;
            form.setFieldValue('businessUnit', businessUnit);
        }

        getIpAddress();

        document.addEventListener('keydown', onKeyDown, false);
        return () => {
            document.removeEventListener('keydown', onKeyDown, false);
            close();
        };
    }, []);

    useEffect(() => {
        setApiBaseUrl(businessUnit == BusinessUnit.Cutting ? flcApiBaseUrl : flpApiBaseUrl);
    }, [businessUnit]);

    const getIpAddress = async () => {
        fetch('https://api.ipify.org/?format=json')
            .then((res) => res.json())
            .then((data: any) => {
                setIpAddress(data?.ip);
            })
            .catch((ex) => {});
    };

    const onKeyDown = (e: any) => {
        if (e.keyCode === 13) {
            form.submit();
        }
    };

    const close = () => {
        setLoading(false);
        form.resetFields();
    };

    const onFinish = (entity: IAuthParams) => {
        setLoading(true);

        const data = {
            businessUnit: entity.businessUnit,
            login: entity.signInLogin,
            password: entity.password,
            ipAddress: ipAddress,
        };

        serverFetch(apiBaseUrl, undefined, `auth/signin`, { method: 'POST', bodyData: data, credentials: 'include' })
            .then((data) => {
                window.accessToken = data.accessToken;

                localStorage.setItem('refreshToken', data.refreshToken);

                const result: any = jwtDecode(data.accessToken);

                let storedBusinessUnit = localStorage.getItem('businessUnit');
                if (!storedBusinessUnit || +storedBusinessUnit != result.businessUnit) {
                    localStorage.setItem('businessUnit', result.businessUnit as any);
                    navigate('/');
                }

                const userSession: IUserSession = {
                    businessUnit: result.businessUnit,
                    userId: result.userId,
                    fullName: result.fullName,
                    type: +result.type as UserType,
                    permissions: tryParseJson(result.permissions),
                    userSettings: tryParseJson(result.userSettings),

                    useTagRequired: false,
                };

                d(userLoaded(userSession));
                close();
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка авторизации', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <div className='auth'>
            <Flex className='auth-container' vertical justify='space-between' align='center'>
                <div className='auth-form'>
                    <div className='auth-logo'>
                        {isNewYearAroundUs() && <div className='santa-hat-auth'></div>}
                        <MainLogoIcon style={{ fontSize: 64 }} />
                    </div>
                    <div className='auth-form-title'>Личный кабинет</div>

                    <Form size={'large'} form={form} initialValues={{ remember: true }} onFinish={onFinish}>
                        <Form.Item name='businessUnit' rules={[{ required: true, message: 'Выберите приложение' }]}>
                            <Select
                                styles={{
                                    root: {
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        color: 'var(--main-green)',
                                        textTransform: 'uppercase',
                                        justifyContent: 'center',
                                    },
                                    content: {
                                        justifyContent: 'center',
                                    },
                                    popup: {
                                        root: {
                                            textTransform: 'uppercase',
                                            textAlign: 'center',
                                        },
                                    },
                                }}
                                placeholder='Приложение'
                                onChange={(value: BusinessUnit) => {
                                    form.setFieldValue('businessUnit', value);
                                }}
                                options={businessUnits.map((t) => {
                                    return { value: t.value, label: t.label };
                                })}
                            />
                        </Form.Item>
                        <Form.Item
                            name='signInLogin'
                            className='auth-form-item'
                            rules={[{ required: true, message: 'Пожалуйста введите логин' }]}
                            hasFeedback
                        >
                            <Input name='signInLogin' placeholder='Логин' disabled={loading} className='auth-form-item-input' />
                        </Form.Item>
                        <Form.Item name='password' rules={[{ required: true, message: 'Пожалуйста введите пароль' }]} hasFeedback>
                            <Input.Password name='password' placeholder={'Пароль'} disabled={loading} className='auth-form-item-input' />
                        </Form.Item>
                        <Form.Item style={{ marginTop: -15, marginLeft: -10, marginBottom: 0, textAlign: 'left' }}>
                            <Button type='link' onClick={() => setShowResetPassword(true)} style={{ color: 'var(--primary-color)' }}>
                                Не помню пароль
                            </Button>
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button type='primary' htmlType='submit' loading={loading} className='auth-form-btn'>
                                Войти
                            </Button>
                        </Form.Item>

                        {businessUnit == BusinessUnit.Cutting ? (
                            <Form.Item style={{ marginTop: 40, textAlign: 'center' }}>
                                <Flex gap='large' align='flex-start' justify='center'>
                                    <a href='https://t.me/FreshLineCuttingBot' target='_blank'>
                                        <img style={{ width: 160 }} alt='Доступно в Telegram' src={TelegramCuttingBotQr} />
                                    </a>
                                    <a href='https://max.ru/id9721145312_1_bot' target='_blank' style={{ color: '#914cf8' }}>
                                        <div
                                            style={{
                                                maxWidth: 160,
                                                maxHeight: 160,
                                                background: '#ffffff',
                                                padding: '15px 5px 0',
                                                borderRadius: 7,
                                            }}
                                        >
                                            <img style={{ width: 128, marginBottom: -10 }} alt='Доступно в MAX' src={MaxCuttingBotQr} />
                                            <span style={{ fontSize: 11 }}>MAXBOTCUTTING</span>
                                        </div>
                                    </a>
                                </Flex>
                            </Form.Item>
                        ) : (
                            <Form.Item style={{ marginTop: 40, textAlign: 'center' }}>
                                <Flex gap='large' align='flex-start' justify='center'>
                                    <a href='https://t.me/FreshLinePlantingBot' target='_blank'>
                                        <img style={{ width: 160 }} alt='Доступно в Telegram' src={TelegramPlantingBotQr} />
                                    </a>
                                    <a href='https://max.ru/id9721145312_2_bot' target='_blank' style={{ color: '#914cf8' }}>
                                        <div
                                            style={{
                                                maxWidth: 160,
                                                maxHeight: 160,
                                                background: '#ffffff',
                                                padding: '15px 5px 0',
                                                borderRadius: 7,
                                            }}
                                        >
                                            <img style={{ width: 128, marginBottom: -10 }} alt='Доступно в MAX' src={MaxPlantingBotQr} />
                                            <span style={{ fontSize: 11 }}>MAXBOTPLANTING</span>
                                        </div>
                                    </a>
                                </Flex>
                            </Form.Item>
                        )}
                    </Form>
                </div>
                <div className='auth-app-version'>Версия {appVersion}</div>
            </Flex>

            {showResetPassword && <ResetPassword api={api} apiBaseUrl={apiBaseUrl} onClose={() => setShowResetPassword(false)} />}

            {contextHolder}
        </div>
    );
};

export default Auth;
